/**
 * Pagination handlers - Data table pagination management
 *
 * These functions manage pagination state for the data table.
 * They work directly with stores.
 */

import i18n from '../../../i18n/core';
import { AppStore } from '../../stores/AppStore';
import { updateUXSetting } from '../../infrastructure/ux-settings';
import * as InteractionHandlers from './interaction-handlers';

/**
 * Update pagination state based on current data
 */
export function updatePagination(): void {
  const currentData = AppStore.currentData.value;
  const pageSize = AppStore.pageSize.value;

  if (!currentData) {
    AppStore.totalPages.value = 1;
    AppStore.currentPage.value = 1;
    return;
  }

  const totalRows = currentData.length;
  AppStore.totalPages.value = Math.max(1, Math.ceil(totalRows / pageSize));

  if (AppStore.currentPage.value > AppStore.totalPages.value) {
    AppStore.currentPage.value = 1;
  }
}

/**
 * Get the current page of data
 */
export function getPaginatedData(): any[] {
  const currentData = AppStore.currentData.value;
  const currentPage = AppStore.currentPage.value;
  const pageSize = AppStore.pageSize.value;

  if (!currentData || currentData.length === 0) return [];

  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  return currentData.slice(start, end);
}

/**
 * Get human-readable pagination info string
 */
export function getPaginationInfo(): string {
  const currentData = AppStore.currentData.value;
  const currentPage = AppStore.currentPage.value;
  const pageSize = AppStore.pageSize.value;

  if (!currentData || currentData.length === 0) return i18n.t('ui:pagination.noData');

  const totalRows = currentData.length;
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalRows);

  return i18n.t('ui:pagination.showing', {
    start: start.toLocaleString(),
    end: end.toLocaleString(),
    total: totalRows.toLocaleString(),
  });
}

/**
 * Go to previous page
 */
export function previousPage(): void {
  if (AppStore.currentPage.value > 1) {
    AppStore.currentPage.value--;
    InteractionHandlers.clearColumnSelection();
  }
}

/**
 * Go to next page
 */
export function nextPage(): void {
  if (AppStore.currentPage.value < AppStore.totalPages.value) {
    AppStore.currentPage.value++;
    InteractionHandlers.clearColumnSelection();
  }
}

/**
 * Go to first page
 */
export function goToFirstPage(): void {
  if (AppStore.currentPage.value !== 1) {
    AppStore.currentPage.value = 1;
    InteractionHandlers.clearColumnSelection();
  }
}

/**
 * Go to last page
 */
export function goToLastPage(): void {
  if (AppStore.currentPage.value !== AppStore.totalPages.value) {
    AppStore.currentPage.value = AppStore.totalPages.value;
    InteractionHandlers.clearColumnSelection();
  }
}

/**
 * Update page size and recalculate pagination
 */
export function updatePageSize(newSize: number | string): void {
  const size = typeof newSize === 'string' ? parseInt(newSize, 10) : newSize;
  if (isNaN(size) || size < 1) return;

  AppStore.pageSize.value = size;
  InteractionHandlers.clearColumnSelection();
  updatePagination();
  updateUXSetting('pagination', 'pageSize', size);
  AppStore.currentPage.value = 1;
  updatePagination();
}
